import type {
	FieldDiaryDisplayStatus,
	FieldDiaryEntryDTO,
	FieldDiarySummary,
} from '@practium/features/api';
import type { FieldDiaryEntry, FieldDiaryStats } from '../types';
import { decodeHtmlEntities, sanitizeNoteHtml } from './html.utils';

const TASK_STATUS_LABEL: Record<Exclude<FieldDiaryDisplayStatus, 'note'>, string> = {
	pending: 'Atrasado',
	upcoming: 'Agendado',
	completed: 'Concluído',
};

export function formatDiaryDate(isoDate: string): string {
	const [year, month, day] = isoDate.split('-');
	return year && month && day ? `${day}/${month}/${year}` : isoDate;
}

export function toMonthKey(year: number, month: number): string {
	return `${year}-${String(month).padStart(2, '0')}`;
}

function toDayMonth(isoDate: string): string {
	const [, month, day] = isoDate.split('-');
	return `${day}/${month}`;
}

function toTaskEntry(
	dto: FieldDiaryEntryDTO,
	status: keyof typeof TASK_STATUS_LABEL
): FieldDiaryEntry {
	return {
		_id: dto._id,
		farmId: dto.prop,
		title: decodeHtmlEntities(dto.title),
		subtitle: `Obrigatório • ${TASK_STATUS_LABEL[status]} (${toDayMonth(dto.scheduledDate)})`,
		status,
		date: dto.scheduledDate,
		type: 'parcelling',
	};
}

export function toNoteEntry(dto: FieldDiaryEntryDTO): FieldDiaryEntry {
	const title = decodeHtmlEntities(dto.title);

	return {
		_id: dto._id,
		farmId: dto.prop,
		title,
		subtitle: `Livre • Registrado (${toDayMonth(dto.scheduledDate)})`,
		status: 'note',
		date: dto.scheduledDate,
		type: 'nota',
		noteData: {
			title,
			date: dto.scheduledDate,
			category: decodeHtmlEntities(dto.category ?? ''),
			note: sanitizeNoteHtml(dto.content ?? ''),
			createdBy: dto.createdByName ?? '',
		},
	};
}

export function toFieldDiaryEntry(dto: FieldDiaryEntryDTO): FieldDiaryEntry | null {
	if (dto.entryType === 'note') return toNoteEntry(dto);
	if (!dto.displayStatus || dto.displayStatus === 'note') return null;
	return toTaskEntry(dto, dto.displayStatus);
}

export function toFieldDiaryEntries(dtos: FieldDiaryEntryDTO[]): FieldDiaryEntry[] {
	return dtos.map(toFieldDiaryEntry).filter((entry): entry is FieldDiaryEntry => entry !== null);
}

export function buildFieldDiaryStats(summary?: FieldDiarySummary): FieldDiaryStats {
	return {
		pending: summary?.pending ?? 0,
		done: summary?.completed ?? 0,
		upcoming: summary?.upcoming ?? 0,
		notes: summary?.notes ?? 0,
		pendingLabel: 'Atrasadas (Sistema)',
		doneLabel: 'Obrigatórias (Sistema)',
		upcomingLabel: 'Próximos dias (Sistema)',
		notesLabel: 'Inserções livres',
	};
}
